import { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, Gift, Copy } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";

export default function BookingSuccess() {
  useSeo({ title: "Booking Confirmed | Full Indoor Air Services" });
  const search = new URLSearchParams(useLocation().search);
  const sessionId = search.get("session_id");

  const [state, setState] = useState({ status: "polling", payment_status: null, attempts: 0 });
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }
    let attempts = 0;
    const MAX = 8;
    const poll = async () => {
      if (stopRef.current) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", booking_id: data.booking_id, amount: data.amount_total });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (attempts >= MAX) {
          setState({ status: "timeout" });
          return;
        }
        setState((s) => ({ ...s, attempts }));
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= MAX) {
          setState({ status: "error", message: "Could not verify payment. Please check your email." });
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  const pending = JSON.parse(window.sessionStorage.getItem("fia_pending_booking") || "null");
  const [copied, setCopied] = useState(false);
  const refCode = pending?.referral_code;
  const credit = pending?.referral_credit || 25;
  const shareLink = refCode ? `${window.location.origin}/book?ref=${refCode}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.warn("Clipboard write failed", e);
    }
  };

  return (
    <div className="bg-bone min-h-[80vh] section-pad" data-testid="booking-success-page">
      <div className="max-w-2xl mx-auto text-center">
        {state.status === "polling" && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-forest/10 text-forest items-center justify-center mb-6">
              <Loader2 className="animate-spin" size={28} />
            </div>
            <h1 className="font-heading text-4xl tracking-tighter font-light">Verifying your deposit…</h1>
            <p className="text-muted2 mt-4">Hold tight, this takes just a moment.</p>
          </>
        )}

        {state.status === "paid" && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-forest text-white items-center justify-center mb-6">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="font-heading text-4xl tracking-tighter font-light">Deposit received. You're booked.</h1>
            <p className="text-muted2 mt-4 max-w-md mx-auto">
              Your slot is locked in. We'll send a confirmation email and call you the day before to finalize details.
            </p>
            <div className="mt-8 inline-flex items-center gap-4 card-surface px-6 py-4">
              {pending?.confirmation_number && (
                <>
                  <div className="text-left">
                    <div className="eyebrow">Confirmation #</div>
                    <div className="font-heading text-xl tracking-widest" data-testid="success-confirmation">{pending.confirmation_number}</div>
                  </div>
                  <div className="w-px h-10 bg-black/10" />
                </>
              )}
              <div className="text-left">
                <div className="eyebrow">Deposit</div>
                <div className="font-heading text-xl">${(state.amount / 100).toFixed(0)}</div>
              </div>
            </div>
            <div className="mt-10 flex gap-3 justify-center flex-wrap">
              <Link to="/" className="btn-ghost" data-testid="success-home">Back to home</Link>
              {pending?.confirmation_number && pending?.email && (
                <Link
                  to={`/my-booking?conf=${pending.confirmation_number}&email=${encodeURIComponent(pending.email)}`}
                  className="btn-primary"
                  data-testid="success-manage-link"
                >
                  Manage my booking
                </Link>
              )}
            </div>

            {refCode && (
              <div className="max-w-xl mx-auto mt-12 card-surface p-8 text-left" data-testid="success-referral-card">
                <div className="flex items-center gap-2">
                  <Gift size={18} className="text-clay" />
                  <span className="eyebrow">Refer a neighbor · earn ${credit}</span>
                </div>
                <h3 className="font-heading text-2xl tracking-tight mt-3">
                  Share your code. You both save ${credit}.
                </h3>
                <p className="text-sm text-muted2 mt-2">
                  When a neighbor books with your code, they get ${credit} off — and you get ${credit} credit on your next service.
                </p>
                <div className="mt-5 flex items-center gap-3 bg-bone border border-black/10 rounded-2xl p-3">
                  <div className="flex-1 px-3">
                    <div className="eyebrow">Your code</div>
                    <div className="font-heading text-2xl tracking-[0.32em]" data-testid="success-referral-code">{refCode}</div>
                  </div>
                  <button onClick={copyLink} className="btn-primary text-sm" data-testid="success-copy-referral">
                    <Copy size={14} /> {copied ? "Copied!" : "Copy link"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {(state.status === "timeout" || state.status === "expired" || state.status === "error") && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-clay/15 text-clay items-center justify-center mb-6">
              <AlertCircle size={28} />
            </div>
            <h1 className="font-heading text-4xl tracking-tighter font-light">
              {state.status === "expired" ? "This checkout expired." : "We couldn't verify payment."}
            </h1>
            <p className="text-muted2 mt-4 max-w-md mx-auto">
              Your booking is still saved. Please check your email — we'll confirm by phone shortly. You can also try the deposit again from the booking flow.
            </p>
            <div className="mt-10">
              <Link to="/book" className="btn-primary" data-testid="success-retry">Back to booking</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
